import React, { useEffect, useMemo, useState } from 'react';
import {
    SafeAreaView,
    View,
    ScrollView,
    useWindowDimensions,
    StyleSheet,
} from 'react-native';
import { useTheme } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { PieChart, BarChart } from 'react-native-chart-kit';

import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import { useAnalytics } from '../../../hooks/useAnalytics';

const EmployeesAnalyticsSection = ({ selectedCompanyCode }) => {
    // -------------------------------------------------------------------------
    // Hooks / theme / i18n
    // -------------------------------------------------------------------------
    const { t } = useTranslation();
    const { colors } = useTheme();
    const styles = makeStyles(colors);
    const { width } = useWindowDimensions();

    // -------------------------------------------------------------------------
    // Analytics API
    // -------------------------------------------------------------------------
    const {
        getEmployeeBreakdown,
        getEmployeeAggregations,
        getEmployeesAgeRange,
        getEmployeesTenureRange,
        getEmployeeAttrition,
        getEmployeesEmploymentTypes,
        getEmployeesQualificationTypes,
    } = useAnalytics();

    // -------------------------------------------------------------------------
    // Constants
    // -------------------------------------------------------------------------
    const currentYear = new Date().getFullYear();

    const GAP = 12;
    const LEFT_COL_WIDTH = 520;
    const MID_COL_WIDTH = 320;
    const RIGHT_COL_WIDTH = 520;

    const TILE_PADDING = 16;
    const SURFACE_PADDING = 8;
    const CHART_HEIGHT = 300;

    const DASHBOARD_FIXED_WIDTH = LEFT_COL_WIDTH + MID_COL_WIDTH + RIGHT_COL_WIDTH + GAP * 2;

    const leftTileInnerWidth = LEFT_COL_WIDTH - TILE_PADDING * 2;
    const midTileInnerWidth = MID_COL_WIDTH - TILE_PADDING * 2;
    const rightTileInnerWidth = RIGHT_COL_WIDTH - TILE_PADDING * 2;

    const leftSurfaceInnerWidth = leftTileInnerWidth - SURFACE_PADDING * 2;
    const midSurfaceInnerWidth = midTileInnerWidth - SURFACE_PADDING * 2;
    const rightSurfaceInnerWidth = rightTileInnerWidth - SURFACE_PADDING * 2;

    const midChartWidth = MID_COL_WIDTH + 150;

    const EMPLOYEE_GROUP_BY_OPTIONS = [
        { label: 'Department', value: 'department' },
        { label: 'Location', value: 'location' },
        { label: 'Seniority', value: 'seniority' },
        { label: 'Nationality', value: 'nationality' },
    ];

    const PIE_COLORS = [
        '#4E79A7',
        '#F28E2B',
        '#E15759',
        '#76B7B2',
        '#59A14F',
        '#EDC948',
        '#B07AA1',
        '#FF9DA7',
        '#9C755F',
        '#BAB0AC',
    ];

    const OTHER_COLOR = '#310404';

    // -------------------------------------------------------------------------
    // State
    // -------------------------------------------------------------------------
    const [groupBy, setGroupBy] = useState('department');
    const [attritionYear, setAttritionYear] = useState(`${currentYear}`);

    const [series, setSeries] = useState([]);
    const [aggregations, setAggregations] = useState(null);
    const [ageSeries, setAgeSeries] = useState([]);
    const [tenureSeries, setTenureSeries] = useState([]);
    const [employmentTypeSeries, setEmploymentTypeSeries] = useState([]);
    const [qualificationTypeSeries, setQualificationTypeSeries] = useState([]);
    const [attrition, setAttrition] = useState(null);

    const [breakdownLoading, setBreakdownLoading] = useState(false);
    const [breakdownError, setBreakdownError] = useState('');

    const [staticLoading, setStaticLoading] = useState(false);
    const [staticError, setStaticError] = useState('');

    const [attritionLoading, setAttritionLoading] = useState(false);
    const [attritionError, setAttritionError] = useState('');

    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------
    const renderCenteredMessage = (styles, message) => (
        <View style={styles.centerBox}>
            <CustomLabel value={message} type="ASSM" />
        </View>
    );

    const toBarChartData = (series = []) => {
        const cleaned = (series || [])
            .map((x) => ({
                label: x?.label ?? x?.Label ?? 'Unknown',
                value: Number(x?.value ?? x?.Value ?? 0),
            }))
            .filter((x) => x.value >= 0);

        return {
            labels: cleaned.map((x) => x.label),
            datasets: [{ data: cleaned.map((x) => x.value) }],
        };
    };

    // -------------------------------------------------------------------------
    // Data fetching
    // -------------------------------------------------------------------------

    // Breakdown chart depends on selected grouping.
    useEffect(() => {
        const loadEmployeesBreakdownOnly = async () => {
            if (!selectedCompanyCode) return;

            setBreakdownLoading(true);
            setBreakdownError('');

            try {
                const res = await getEmployeeBreakdown(selectedCompanyCode, groupBy);
                const payload = res?.data ?? res;
                setSeries(Array.isArray(payload) ? payload : []);
            } catch (e) {
                setBreakdownError(e?.message || 'Failed to load breakdown');
            } finally {
                setBreakdownLoading(false);
            }
        };

        loadEmployeesBreakdownOnly();
    }, [selectedCompanyCode, groupBy]);

    // Static analytics are loaded together once company changes.
    useEffect(() => {
        const loadEmployeesAnalyticsStatic = async () => {
            if (!selectedCompanyCode) return;

            setStaticLoading(true);
            setStaticError('');

            try {
                const [aggRes, ageRes, tenureRes, empTypeRes, qualTypeRes] = await Promise.all([
                    getEmployeeAggregations(selectedCompanyCode),
                    getEmployeesAgeRange(selectedCompanyCode),
                    getEmployeesTenureRange(selectedCompanyCode),
                    getEmployeesEmploymentTypes(selectedCompanyCode),
                    getEmployeesQualificationTypes(selectedCompanyCode),
                ]);

                setAggregations(aggRes?.data ?? aggRes ?? null);
                setAgeSeries(Array.isArray(ageRes?.data ?? ageRes) ? ageRes?.data ?? ageRes : []);
                setTenureSeries(Array.isArray(tenureRes?.data ?? tenureRes) ? tenureRes?.data ?? tenureRes : []);
                setEmploymentTypeSeries(Array.isArray(empTypeRes?.data ?? empTypeRes) ? empTypeRes?.data ?? empTypeRes : []);
                setQualificationTypeSeries(Array.isArray(qualTypeRes?.data ?? qualTypeRes) ? qualTypeRes?.data ?? qualTypeRes : []);
            } catch (e) {
                setStaticError(e?.message || 'Failed to load analytics');
            } finally {
                setStaticLoading(false);
            }
        };

        loadEmployeesAnalyticsStatic();
    }, [selectedCompanyCode]);

    // Attrition is year-dependent.
    useEffect(() => {
        const loadAttrition = async () => {
            if (!selectedCompanyCode) return;

            setAttritionError('');
            setAttritionLoading(true);

            try {
                const res = await getEmployeeAttrition(selectedCompanyCode, Number(attritionYear));
                const payload = res?.data ?? res;
                setAttrition(payload ?? null);
            } catch (e) {
                setAttrition(null);
                setAttritionError(e?.message || 'Failed to load attrition');
            } finally {
                setAttritionLoading(false);
            }
        };

        loadAttrition();
    }, [selectedCompanyCode, attritionYear]);

    // -------------------------------------------------------------------------
    // Derived data
    // -------------------------------------------------------------------------
    const groupByDropdownOptions = EMPLOYEE_GROUP_BY_OPTIONS.map((option) => ({
        label: option.label,
        value: option.value,
    }));

    const YEAR_OPTIONS = useMemo(() => {
        const years = [];
        const COUNT = 200;

        for (let i = 0; i < COUNT; i++) {
            const year = currentYear - i;
            years.push({ label: `${year}`, value: `${year}` });
        }

        return years;
    }, [currentYear]);

    const pieData = useMemo(() => {
        const TOP_N = 10;

        const cleaned = (series || [])
            .map((item) => ({
                label: item?.label ?? 'Unknown',
                value: Number(item?.value ?? 0),
            }))
            .filter((item) => item.value > 0);

        const top = cleaned.slice(0, TOP_N);
        const rest = cleaned.slice(TOP_N);
        const restSum = rest.reduce((acc, item) => acc + item.value, 0);
        const finalData = restSum > 0 ? [...top, { label: 'Other', value: restSum }] : top;

        return finalData.map((item, index) => ({
            name: item.label,
            population: item.value,
            color: item.label === 'Other' ? OTHER_COLOR : PIE_COLORS[index % PIE_COLORS.length],
            legendFontColor: colors.text,
            legendFontSize: 12,
        }));
    }, [series, colors.text]);

    const ageBarData = useMemo(() => toBarChartData(ageSeries), [ageSeries]);
    const tenureBarData = useMemo(() => toBarChartData(tenureSeries), [tenureSeries]);
    const qualificationTypeBarData = useMemo(() => toBarChartData(qualificationTypeSeries), [qualificationTypeSeries]);

    const employmentNormalized = useMemo(() => {
        return (employmentTypeSeries || [])
            .map((item) => ({
                label: item?.label ?? item?.Label ?? 'Unknown',
                value: Number(item?.value ?? item?.Value ?? 0),
            }))
            .filter((item) => item.value > 0);
    }, [employmentTypeSeries]);

    const employmentPieData = useMemo(() => {
        return employmentNormalized.map((item, index) => ({
            name: item.label,
            population: item.value,
            color: PIE_COLORS[index % PIE_COLORS.length],
            legendFontColor: colors.text,
            legendFontSize: 12,
        }));
    }, [employmentNormalized, colors.text]);

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------
    return (
        <SafeAreaView style={styles.additionalCard}>
            {!selectedCompanyCode ? (
                renderCenteredMessage(styles, t('common:loadingselectedCompany'))
            ) : (
                <ScrollView
                    horizontal={width < DASHBOARD_FIXED_WIDTH}
                    showsHorizontalScrollIndicator={width < DASHBOARD_FIXED_WIDTH}
                    contentContainerStyle={{ flexGrow: 1 }}
                >
                    <ScrollView
                        nestedScrollEnabled
                        showsVerticalScrollIndicator
                        contentContainerStyle={{ flexGrow: 1 }}
                    >
                        <View style={[styles.dashboardOuter, { minWidth: DASHBOARD_FIXED_WIDTH }]}>
                            <View style={[styles.dashboardRow, { flexWrap: 'nowrap' }]}>
                                {/* -----------------------------------------------------------------
                                    LEFT COLUMN
                                    ----------------------------------------------------------------- */}
                                <View style={[styles.col, { width: LEFT_COL_WIDTH }]}>
                                    {/* KPI summary cards */}
                                    <View style={styles.kpiRow}>
                                        <View style={styles.kpiCard}>
                                            <CustomLabel value={t('navigate:employees')} type="LABEL" />
                                            <CustomLabel value={`${aggregations?.totalEmployees ?? '-'}`} type="MINORTITLE" />
                                        </View>

                                        <View style={styles.kpiCard}>
                                            <CustomLabel value={t('navigate:averageAge')} type="LABEL" />
                                            <CustomLabel
                                                value={
                                                    aggregations?.averageAge === null || aggregations?.averageAge === undefined
                                                        ? '-'
                                                        : `${Number(aggregations.averageAge).toFixed(1)}`
                                                }
                                                type="MINORTITLE"
                                            />
                                        </View>

                                        <View style={styles.kpiCard}>
                                            <CustomLabel
                                                value={t('navigate:averageWorkingYears')}
                                                type="LABEL"
                                                textStyle={{ fontSize: 17, lineHeight: 15 }}
                                            />
                                            <CustomLabel
                                                value={
                                                    aggregations?.averageWorkingYears === null || aggregations?.averageWorkingYears === undefined
                                                        ? '-'
                                                        : `${Number(aggregations.averageWorkingYears).toFixed(1)}`
                                                }
                                                type="MINORTITLE"
                                            />
                                        </View>
                                    </View>

                                    {/* Employment types */}
                                    <View style={[styles.tile, styles.employmentTile]}>
                                        <CustomLabel value={t('navigate:employmentTypes') || 'Employment Types'} type="LABEL" />

                                        {staticLoading ? (
                                            renderCenteredMessage(styles, t('common:loading'))
                                        ) : staticError ? (
                                            renderCenteredMessage(styles, staticError)
                                        ) : employmentPieData.length === 0 ? (
                                            renderCenteredMessage(styles, t('common:noData') || 'No data')
                                        ) : (
                                            <View style={styles.donutWrap}>
                                                <PieChart
                                                    data={employmentPieData}
                                                    width={leftSurfaceInnerWidth}
                                                    height={CHART_HEIGHT / 2 - 40}
                                                    accessor="population"
                                                    backgroundColor="transparent"
                                                    paddingLeft="10"
                                                    absolute
                                                    chartConfig={{
                                                        backgroundGradientFrom: 'transparent',
                                                        backgroundGradientTo: 'transparent',
                                                        color: () => colors.text,
                                                        labelColor: () => colors.text,
                                                    }}
                                                />
                                            </View>
                                        )}
                                    </View>

                                    {/* Age ranges */}
                                    <View style={[styles.tile, styles.ageTile]}>
                                        <CustomLabel value={t('navigate:ageRanges')} type="LABEL" />

                                        {staticLoading ? (
                                            renderCenteredMessage(styles, t('common:loading'))
                                        ) : staticError ? (
                                            renderCenteredMessage(styles, staticError)
                                        ) : ageBarData?.labels?.length === 0 ? (
                                            renderCenteredMessage(styles, t('common:noData') || 'No data')
                                        ) : (
                                            <View style={styles.chartSurface}>
                                                <BarChart
                                                    data={ageBarData}
                                                    width={leftSurfaceInnerWidth}
                                                    height={CHART_HEIGHT - 55}
                                                    fromZero
                                                    withInnerLines={false}
                                                    verticalLabelRotation={0}
                                                    segment={4}
                                                    showValuesOnTopOfBars
                                                    chartConfig={{
                                                        backgroundGradientFrom: 'transparent',
                                                        backgroundGradientTo: 'transparent',
                                                        color: (opacity = 1) => `rgba(0, 128, 0, ${opacity})`,
                                                        labelColor: () => colors.text,
                                                        propsForLabels: { fontSize: 10, fontWeight: '600' },
                                                        propsForBackgroundLines: { stroke: 'transparent' },
                                                        decimalPlaces: 0,
                                                    }}
                                                    style={{ marginTop: 8 }}
                                                />
                                            </View>
                                        )}
                                    </View>
                                </View>

                                {/* -----------------------------------------------------------------
                                    MIDDLE COLUMN
                                    ----------------------------------------------------------------- */}
                                <View style={[styles.col, { width: MID_COL_WIDTH }]}>
                                    {/* Qualification types */}
                                    <View style={[styles.tile, { minHeight: 310 }]}>
                                        <CustomLabel value={t('navigate:qualificationTypes') || 'Qualification Types'} type="LABEL" />

                                        {staticLoading ? (
                                            renderCenteredMessage(styles, t('common:loading'))
                                        ) : staticError ? (
                                            renderCenteredMessage(styles, staticError)
                                        ) : qualificationTypeBarData?.labels?.length === 0 ? (
                                            renderCenteredMessage(styles, t('common:noData') || 'No data')
                                        ) : (
                                            <View style={styles.chartSurface}>
                                                <BarChart
                                                    data={qualificationTypeBarData}
                                                    width={width >= DASHBOARD_FIXED_WIDTH ? midChartWidth + 50 : midSurfaceInnerWidth}
                                                    height={CHART_HEIGHT - 100}
                                                    fromZero
                                                    withInnerLines={false}
                                                    verticalLabelRotation={15}
                                                    showValuesOnTopOfBars
                                                    yAxisLabel=""
                                                    yAxisSuffix=""
                                                    segments={4}
                                                    chartConfig={{
                                                        backgroundGradientFrom: 'transparent',
                                                        backgroundGradientTo: 'transparent',
                                                        color: (opacity = 1) => `rgba(0, 128, 0, ${opacity})`,
                                                        labelColor: () => colors.text,
                                                        propsForLabels: { fontSize: 8, fontWeight: '600' },
                                                        propsForBackgroundLines: { stroke: 'transparent' },
                                                        decimalPlaces: 0,
                                                    }}
                                                    style={{ alignSelf: 'center' }}
                                                />
                                            </View>
                                        )}
                                    </View>

                                    {/* Tenure ranges */}
                                    <View style={[styles.tile, { minHeight: 350 }]}>
                                        <CustomLabel value={t('navigate:tenureRanges')} type="LABEL" />

                                        {staticLoading ? (
                                            renderCenteredMessage(styles, t('common:loading'))
                                        ) : staticError ? (
                                            renderCenteredMessage(styles, staticError)
                                        ) : tenureBarData?.labels?.length === 0 ? (
                                            renderCenteredMessage(styles, t('common:noData') || 'No data')
                                        ) : (
                                            <View style={styles.chartSurface}>
                                                <BarChart
                                                    data={tenureBarData}
                                                    width={width >= DASHBOARD_FIXED_WIDTH ? midChartWidth : midSurfaceInnerWidth}
                                                    height={CHART_HEIGHT - 50}
                                                    fromZero
                                                    withInnerLines={false}
                                                    verticalLabelRotation={0}
                                                    showValuesOnTopOfBars
                                                    yAxisLabel=""
                                                    yAxisSuffix=""
                                                    segments={4}
                                                    chartConfig={{
                                                        backgroundGradientFrom: 'transparent',
                                                        backgroundGradientTo: 'transparent',
                                                        color: (opacity = 1) => `rgba(0, 128, 0, ${opacity})`,
                                                        labelColor: () => colors.text,
                                                        propsForLabels: { fontSize: 10, fontWeight: '600' },
                                                        propsForBackgroundLines: { stroke: 'transparent' },
                                                        decimalPlaces: 0,
                                                    }}
                                                    style={{ alignSelf: 'center' }}
                                                />
                                            </View>
                                        )}
                                    </View>
                                </View>

                                {/* -----------------------------------------------------------------
                                    RIGHT COLUMN
                                    ----------------------------------------------------------------- */}
                                <View style={[styles.col, { width: RIGHT_COL_WIDTH }]}>
                                    {/* Top summary row: attrition + gender */}
                                    <View style={[styles.rightTopRow, styles.topRowHeight]}>
                                        {/* Attrition */}
                                        <View style={styles.halfTile}>
                                            <View style={styles.halfTileHeader}>
                                                <CustomLabel value={t('navigate:attritionRate') || 'Attrition Rate'} type="LABEL" />

                                                <View style={{ width: Math.min(140, RIGHT_COL_WIDTH * 0.45) }}>
                                                    <CustomDropDown
                                                        defValue={attritionYear}
                                                        options={YEAR_OPTIONS}
                                                        setValue={setAttritionYear}
                                                        type="MAININPUT"
                                                        label={t('common:year') || 'Year'}
                                                    />
                                                </View>
                                            </View>

                                            {attritionLoading ? (
                                                renderCenteredMessage(styles, t('common:loading'))
                                            ) : attritionError ? (
                                                renderCenteredMessage(styles, attritionError)
                                            ) : !attrition ? (
                                                renderCenteredMessage(styles, t('common:noData') || 'No data')
                                            ) : (
                                                <>
                                                    <View style={styles.attritionBig}>
                                                        <CustomLabel
                                                            value={`${Number(attrition.attritionRatePct ?? 0).toFixed(2)}%`}
                                                            type="MINORTITLE"
                                                            textStyle={{ fontSize: 34, lineHeight: 36 }}
                                                        />
                                                    </View>

                                                    <View style={styles.attritionStatsRow}>
                                                        <View style={styles.attritionStat}>
                                                            <CustomLabel value={t('navigate:leavers') || 'Leavers'} type="ASSM" />
                                                            <CustomLabel value={`${attrition.leavers ?? 0}`} type="LABEL" />
                                                        </View>

                                                        <View style={styles.attritionDivider} />

                                                        <View style={[styles.attritionStat, { flex: 2 }]}>
                                                            <CustomLabel value={t('navigate:avgHeadcount') || 'Avg Headcount'} type="ASSM" />
                                                            <CustomLabel
                                                                value={`${Number(attrition.avgHeadcount ?? 0).toFixed(1)}`}
                                                                type="LABEL"
                                                            />
                                                        </View>
                                                    </View>
                                                </>
                                            )}
                                        </View>

                                        {/* Gender breakdown */}
                                        <View style={styles.halfTile}>
                                            <CustomLabel
                                                value={t('navigate:genderBreakdown')}
                                                type="LABEL"
                                                textStyle={{ fontSize: 16, lineHeight: 13, paddingBottom: 15 }}
                                            />

                                            {(() => {
                                                const male = Number(aggregations?.maleEmployees ?? 0);
                                                const female = Number(aggregations?.femaleEmployees ?? 0);
                                                const total = male + female || 1;

                                                const malePercent = (male / total) * 100;
                                                const femalePercent = (female / total) * 100;

                                                return (
                                                    <>
                                                        <View style={styles.genderStack}>
                                                            <View style={[styles.femaleSelection, { flex: female }]}>
                                                                <CustomLabel
                                                                    value={`${t('navigate:female')} ${femalePercent.toFixed(0)}%`}
                                                                    type="ASSM"
                                                                />
                                                            </View>

                                                            <View style={[styles.maleSelection, { flex: male }]}>
                                                                <CustomLabel
                                                                    value={`${t('navigate:male')} ${malePercent.toFixed(0)}%`}
                                                                    type="ASSM"
                                                                />
                                                            </View>
                                                        </View>

                                                        <View style={styles.genderNumbers}>
                                                            <CustomLabel value={`${male} ${t('navigate:male')}`} type="ASSM" />
                                                            <CustomLabel value={`${female} ${t('navigate:female')}`} type="ASSM" />
                                                        </View>
                                                    </>
                                                );
                                            })()}
                                        </View>
                                    </View>

                                    {/* Breakdown pie chart */}
                                    <View style={styles.tile}>
                                        <View style={styles.tileHeader}>
                                            <CustomLabel value={t('navigate:groupBy')} type="LABEL" />
                                            <CustomDropDown
                                                defValue={groupBy}
                                                options={groupByDropdownOptions}
                                                setValue={setGroupBy}
                                                type="MYPROFILE"
                                                label={t('navigate:groupBy')}
                                            />
                                        </View>

                                        <View style={styles.tileBody}>
                                            {breakdownLoading ? (
                                                renderCenteredMessage(styles, t('common:loading'))
                                            ) : breakdownError ? (
                                                renderCenteredMessage(styles, breakdownError)
                                            ) : pieData.length === 0 ? (
                                                renderCenteredMessage(styles, t('common:noData') || 'No data')
                                            ) : (
                                                <View style={styles.chartSurface}>
                                                    <PieChart
                                                        data={pieData}
                                                        width={rightSurfaceInnerWidth}
                                                        height={CHART_HEIGHT}
                                                        accessor="population"
                                                        backgroundColor="transparent"
                                                        paddingLeft="10"
                                                        absolute
                                                        chartConfig={{
                                                            backgroundGradientFrom: 'transparent',
                                                            backgroundGradientTo: 'transparent',
                                                            color: () => colors.text,
                                                            labelColor: () => colors.text,
                                                        }}
                                                    />
                                                </View>
                                            )}
                                        </View>
                                    </View>
                                </View>
                            </View>
                        </View>
                    </ScrollView>
                </ScrollView>
            )}
        </SafeAreaView>
    );
};

const makeStyles = (colors) =>
    StyleSheet.create({
        additionalCard: {
            flex: 1,
            marginVertical: 10,
            marginHorizontal: 30,
            padding: 12,
            borderRadius: 8,
            backgroundColor: colors.card ?? '#fff',
        },

        centerBox: {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
        },

        dashboardOuter: {
            flex: 1,
            alignItems: 'stretch',
        },

        dashboardRow: {
            flex: 1,
            width: '100%',
            flexDirection: 'row',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            gap: 12,
        },

        col: {
            flex: 1,
            height: '100%',
            alignSelf: 'stretch',
            gap: 12,
        },

        kpiRow: {
            flexDirection: 'row',
            gap: 10,
            alignItems: 'stretch',
        },

        kpiCard: {
            flex: 1,
            padding: 10,
            minWidth: 140,
            maxWidth: 220,
            backgroundColor: '#d4efd5',
            borderRadius: 12,
        },

        tile: {
            borderWidth: 1,
            borderColor: colors.border ?? '#E5E7EB',
            borderRadius: 16,
            padding: 16,
            backgroundColor: colors.card ?? '#fcf9f9',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },

        employmentTile: {
            height: 210,
        },

        ageTile: {
            height: 350,
        },

        tileHeader: {
            width: '100%',
            maxWidth: 420,
        },

        tileBody: {
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 3,
        },

        donutWrap: {
            marginTop: 12,
            alignSelf: 'center',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            paddingVertical: 10,
            paddingHorizontal: 8,
            borderRadius: 12,
            backgroundColor: 'white',
        },

        rightTopRow: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 12,
        },

        topRowHeight: {
            height: 220,
        },

        halfTile: {
            width: '49%',
            borderWidth: 1,
            borderColor: colors.border ?? '#ddd',
            borderRadius: 12,
            padding: 10,
            backgroundColor: colors.card ?? '#fff',
        },

        halfTileHeader: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
        },

        attritionBig: {
            alignItems: 'center',
            height: 70,
            justifyContent: 'center',
            paddingVertical: 6,
            borderRadius: 10,
            backgroundColor: '#d4efd5',
            borderWidth: 1,
            borderColor: '#E3EAFD',
        },

        attritionStatsRow: {
            marginTop: 4,
            height: 60,
            flexDirection: 'row',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            overflow: 'hidden',
        },

        attritionStat: {
            flex: 1,
            paddingVertical: 10,
            paddingHorizontal: 10,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.card ?? '#fff',
        },

        attritionDivider: {
            width: 1,
            backgroundColor: colors.border ?? '#E5E7EB',
        },

        chartSurface: {
            marginTop: 1,
            alignSelf: 'stretch',
            paddingVertical: 10,
            paddingHorizontal: 8,
            paddingBottom: 10,
            borderRadius: 12,
            backgroundColor: 'white',
            borderColor: '#E3EAFD',
        },

        genderStack: {
            width: '100%',
            height: 120,
            alignSelf: 'stretch',
            marginTop: 0,
            borderRadius: 8,
            overflow: 'hidden',
            borderWidth: 1,
            borderColor: colors.border ?? '#ddd',
        },

        maleSelection: {
            backgroundColor: '#4E79A7',
            justifyContent: 'center',
            alignItems: 'center',
        },

        femaleSelection: {
            backgroundColor: '#E15759',
            justifyContent: 'center',
            alignItems: 'center',
        },

        genderNumbers: {
            marginTop: 8,
            flexDirection: 'row',
            justifyContent: 'space-around',
            alignItems: 'center',
        },
    });

export default EmployeesAnalyticsSection;